// Esperar a que el servidor termine de leer un archivo recién subido (5.2).
//
// **Por qué hay que esperar**: `on_upload` corre en una tarea de Celery que
// llama al modelo, así que cuando la subida responde la propuesta todavía no
// existe. Preguntar una sola vez es preguntar siempre demasiado pronto.
//
// Vive aparte del hook por una razón de prueba y no de orden: el bucle es lo
// único que tiene tiempo dentro, y con el reloj falso de Vitest el `waitFor` de
// Testing Library se queda esperando un temporizador que nadie avanza —las tres
// pruebas agotaban el minuto—. Con la espera inyectada, esto se prueba entero y
// sin relojes.

import type { Pendiente } from '@/services/workspacesService'

/** Cada cuánto se vuelve a preguntar, y cuántas veces. Veinte por tres
 *  segundos es un minuto: más no se espera, porque la propuesta no se pierde
 *  —sigue en la puerta de «Lo que sabe tu agente»— y callar es peor. */
export const PROPOSAL_POLL_MS = 3000
export const PROPOSAL_ATTEMPTS = 20

const sleep = (ms: number) => new Promise((done) => setTimeout(done, ms))

/**
 * Lo que espera un sí o un no y salió **de este archivo**, cuando llegue.
 *
 * Devuelve la lista vacía si se agotan los intentos. Si `ask` falla, el fallo
 * sube: quien llama decide qué decir, y aquí no hay nada que decidir.
 */
export async function waitForProposals({
  documentId,
  ask,
  attempts = PROPOSAL_ATTEMPTS,
  everyMs = PROPOSAL_POLL_MS,
  wait = sleep,
}: {
  documentId: string
  ask: () => Promise<Pendiente[]>
  attempts?: number
  everyMs?: number
  wait?: (ms: number) => Promise<unknown>
}): Promise<Pendiente[]> {
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    // La primera vuelta va sin esperar: un archivo que ya está leído —porque se
    // subió dos veces, o porque el modelo fue rápido— no tiene por qué costar
    // tres segundos de silencio.
    if (attempt > 0) await wait(everyMs)

    const waiting = await ask()
    const fromThisFile = waiting.filter(
      (one) => one.documento != null && String(one.documento) === documentId,
    )
    if (fromThisFile.length > 0) return fromThisFile
  }

  return []
}

/** Cuánto espera el mensaje del clip a que el documento se pueda leer: 30 s.
 *  Medido el 2026-09-28 (brain-multimodal, 1.5): un PDF normal tarda 6 s y
 *  uno de cien páginas, tres minutos. Pasado el techo el mensaje sale igual, y
 *  el Brain dice que se está procesando en vez de contestar sin leerlo. */
export const PROCESSED_POLL_MS = 1500
export const PROCESSED_ATTEMPTS = 20

/** Terminó de leerse -para bien o para mal- o se agotó la espera. */
const READ = new Set(['embedded', 'failed'])

/**
 * Espera a que el documento recién subido termine de procesarse (D2).
 *
 * Hasta el 2026-09-28 el mensaje «He subido X» salía a los 0,05 s de la
 * subida, así que el Brain lo recibía antes de que hubiera texto que leer,
 * incluso con un PDF de seis segundos. Devuelve el último estado visto. Si no
 * se puede preguntar, deja de esperar: esperar a ciegas solo retrasaría un
 * mensaje que va a salir igual.
 */
export async function waitUntilProcessed({
  ask,
  attempts = PROCESSED_ATTEMPTS,
  everyMs = PROCESSED_POLL_MS,
  wait = sleep,
}: {
  ask: () => Promise<{ status?: string }>
  attempts?: number
  everyMs?: number
  wait?: (ms: number) => Promise<unknown>
}): Promise<string | undefined> {
  let status: string | undefined
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    if (attempt > 0) await wait(everyMs)
    try {
      status = (await ask()).status
    } catch {
      return status
    }
    if (status && READ.has(status)) return status
  }
  return status
}
